'use client'

import { isbot } from 'isbot'
import Cookies from 'js-cookie'

import { COOKIE_NAMES } from 'lib/constants'

const BOT_LIKE_USER_AGENTS = [
  'Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/118.0.0.0 Safari/537.36',
]

const ISSUER_BOTS_BY_IP_ADDRESS = [
  {
    issuerBotName: 'Performline',
    issuerBotIpAddress: '52.7.243.144',
  },
  {
    issuerBotName: 'Rakuten Compliance Crawler',
    issuerBotIpAddress: '35.245.243.139',
  },
  {
    issuerBotName: 'Rakuten Compliance Crawler or rm-gcp-compliance-apps',
    issuerBotIpAddress: '34.66.179.215',
  },
  {
    issuerBotName: 'rm-gcp-compliance-apps',
    issuerBotIpAddress: '35.223.85.220',
  },
] as const satisfies readonly {
  issuerBotName: string
  issuerBotIpAddress: string
}[]

const ISSUER_BOTS_BY_USER_AGENT_TEST = [
  {
    issuerBotName: 'impact.com agent',
    getIsIssuerBot: (userAgent) =>
      /compatible;impact.com agent/.test(userAgent),
  },
] as const satisfies readonly {
  issuerBotName: string
  getIsIssuerBot: (userAgent: string) => boolean
}[]

type IssuerBotInfo<_IsIssuerBot extends boolean> = _IsIssuerBot extends true
  ? { isIssuerBot: true; issuerBotName: string }
  : { isIssuerBot: false; issuerBotName?: never }

/**
 * Checks whether the user is an issuer bot. Check is performed both by user
 * agent and by IP address.
 */
const getIssuerBotInfo = ({
  userAgent,
  userIpAddress,
}: {
  userAgent: string
  userIpAddress?: string
}): IssuerBotInfo<boolean> => {
  const issuerBotNameFoundByUserAgent =
    ISSUER_BOTS_BY_USER_AGENT_TEST.find(({ getIsIssuerBot }) =>
      getIsIssuerBot(userAgent),
    )?.issuerBotName ?? null

  const issuerBotNameFoundByIpAddress =
    ISSUER_BOTS_BY_IP_ADDRESS.find(
      ({ issuerBotIpAddress }) => issuerBotIpAddress === userIpAddress,
    )?.issuerBotName ?? null

  const issuerBotName =
    issuerBotNameFoundByUserAgent || issuerBotNameFoundByIpAddress

  return issuerBotName
    ? { isIssuerBot: true, issuerBotName }
    : { isIssuerBot: false }
}

type BotInfo<_IsIssuerBot extends boolean> = _IsIssuerBot extends true
  ? { isBot: true } & IssuerBotInfo<true>
  : { isBot: boolean } & IssuerBotInfo<false>

export const getBotInfo = (): BotInfo<boolean> => {
  const { userAgent } = navigator
  const userIpAddress = Cookies.get(COOKIE_NAMES.userIpAddress)

  const isBotLikeUserAgent =
    isbot(userAgent) || BOT_LIKE_USER_AGENTS.includes(userAgent)

  const issuerBotInfo = getIssuerBotInfo({
    userAgent,
    userIpAddress,
  })
  const { isIssuerBot } = issuerBotInfo

  return isIssuerBot
    ? { isBot: true, ...issuerBotInfo }
    : { isBot: isBotLikeUserAgent, ...issuerBotInfo }
}
